import {
  type CreateTableOverlayConfig,
  createTableOverlay,
} from "@/components/discovery/table-overlays/createTableOverlay";
import type { SocialMetrics } from "@/components/ui/molecules/feed-article-tooltips/SocialEngagementTooltip";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useNewsFeedParamsStore from "@/store/news-feed/params/newsFeed-params.slice";

/**
 * Type for feed articles table row data
 */
export type FeedArticleRowData = {
  DATE: string;
  PUBLICATION: string;
  HEADLINE: string;
  AUTHOR: string;
  IMPACT_SCORE: number | null;
  SENTIMENT: string;
  sentimentRationale: string | null;
  PROMINENCE: string;
  TIER: string;
  READERSHIP: number | null;
  DOMAIN_AUTHORITY: number | null;
  SOCIAL_ENGAGEMENTS: number | null;
  socialMetrics: SocialMetrics | null;
  CATEGORY: string;
  COUNTRY: string;
  REGION: string;
  LANGUAGE: string;
  NARRATIVE_CLUSTERS: string;
  URL: string;
  FIRST_MENTION: string | null;
  FIRST_MENTION_TERM: string | null;
};

const useArticlesTableState: CreateTableOverlayConfig["useTableState"] = () => {
  const error = useDiscoveryContentManagementStore((state) => state.error);
  const isFetchingArticles = useDiscoveryContentManagementStore(
    (state) => state.isFetchingArticles,
  );
  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalCount,
  );
  const isInitialLoading = useDiscoveryContentManagementStore(
    (state) => state.isInitialLoading,
  );
  return {
    error,
    isFetching: isFetchingArticles,
    totalCount,
    isInitialLoading,
  };
};

const useArticlesNoRowsActions: CreateTableOverlayConfig["useNoRowsActions"] =
  () => {
    const selectedFilters = useNewsFeedParamsStore(
      (state) => state.selectedFilters,
    );
    const clearAllSelectedFilters = useNewsFeedParamsStore(
      (state) => state.clearAllSelectedFilters,
    );
    const searchTerm = useDiscoveryContentManagementStore(
      (state) => state.search.searchTerm,
    );
    const setSearchTerm = useDiscoveryContentManagementStore(
      (state) => state.setSearchTerm,
    );
    const resetState = useDiscoveryContentManagementStore(
      (state) => state.resetState,
    );
    const isAppliedFilters = selectedFilters.size > 0 || !!searchTerm;
    const onReset = () => {
      clearAllSelectedFilters();
      setSearchTerm("");
      resetState();
    };
    return { isAppliedFilters, onReset };
  };

const useArticlesClearError: CreateTableOverlayConfig["useClearError"] = () => {
  const setError = useDiscoveryContentManagementStore(
    (state) => state.setError,
  );
  return () => setError(null);
};

const articlesTableOverlayConfig: CreateTableOverlayConfig = {
  useTableState: useArticlesTableState,
  noRowsMessageWithFilters: "No Articles Found for Applied Filters",
  noRowsMessageWithoutFilters: "No Articles Found",
  useNoRowsActions: useArticlesNoRowsActions,
  errorTitle: "Failed to Load Articles",
  useClearError: useArticlesClearError,
};

/**
 * Creates a unified overlay component for the feed articles table.
 * Used as the noRowsOverlayComponent for AG Grid.
 */
export const createUnifiedOverlay = (onRetry?: () => void) =>
  createTableOverlay(articlesTableOverlayConfig, onRetry);
